import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import '../pricing/qfaa-pricing.css';
import './qfaa-upgrade.css';

const CHECK = (
  <span className="check">
    <svg viewBox="0 0 24 24" fill="none" stroke="#7ff0b0" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 13l4 4L19 7" />
    </svg>
  </span>
);

const CONTACT_URL = 'https://www.jtncgroup.com/contact';

const ANNUAL = { 2: 7900, 3: 19000 };

const TIERS = [
  {
    id: 'tier1',
    no: 'Tier 1',
    title: 'Getting started',
    priceLabel: 'Included',
    priceFree: true,
    risk: 500,
    riskLabel: (
      <>
        Up to <b>$500</b> risk per trade
      </>
    ),
    sub: 'The standard QFAA Tier included with every plan.',
    features: ['Full Quantum Flo Auto Algo engine', 'Trades SPY & QQQ (SPX when available)'],
    saveNote: null,
    proNote: null,
    singlePayNote: null,
  },
  {
    id: 'tier2',
    no: 'Tier 2',
    title: 'Growing account',
    priceLabel: '$7,900',
    priceFree: false,
    priceSuffix: 'per year',
    risk: 1000,
    riskLabel: (
      <>
        Raises your cap to <b>$1,000</b> risk per trade
      </>
    ),
    sub: 'Double your per-trade sizing as your balance builds.',
    features: ['Everything in Tier 1', 'Larger position sizing on every signal'],
    saveNote: {
      text: '★ 17% better value than Tier\u00a01',
      title:
        'Tier 1: $12,000 for $500 risk = $24 per $1 of risk. Tier 2: $19,900 total ($12,000 + $7,900) for $1,000 risk = $19.90 per $1 of risk — 17% lower.',
    },
    proNote: (
      <>
        <b>1, 3, 6, or 12-month</b> terms — pick yours below.
      </>
    ),
    singlePayNote: 'your Quantum Echo & Quantum Superstars move up to Tier 2 as well.',
    featured: true,
  },
  {
    id: 'tier3',
    no: 'Tier 3',
    title: 'Maximum sizing',
    priceLabel: '$19,000',
    priceFree: false,
    priceSuffix: 'per year',
    risk: 2000,
    riskLabel: (
      <>
        Raises your cap to <b>$2,000</b> risk per trade
      </>
    ),
    sub: 'Our highest risk-per-trade cap for larger accounts.',
    features: ['Everything in Tier 2', 'Maximum position sizing on every signal'],
    saveNote: {
      text: '★ 35% better value than Tier\u00a01',
      title:
        'Tier 1: $12,000 for $500 risk = $24 per $1 of risk. Tier 3: $31,000 total ($12,000 + $19,000) for $2,000 risk = $15.50 per $1 of risk — 35% lower.',
    },
    proNote: (
      <>
        <b>1, 3, 6, or 12-month</b> terms — pick yours below.
      </>
    ),
    singlePayNote: 'your Quantum Echo & Quantum Superstars move up to Tier 3 as well.',
  },
];

const roundUp25 = (x) => Math.ceil(x / 25) * 25;

const formatMoney = (n) =>
  `$${n.toLocaleString('en-US')}`;

const normalizeTier = (tier) => {
  if (!tier) return 'tier1';
  const t = String(tier).toLowerCase().replace(/\s+/g, '');
  if (t === '1' || t === 'tier1') return 'tier1';
  if (t === '2' || t === 'tier2') return 'tier2';
  if (t === '3' || t === 'tier3') return 'tier3';
  return 'tier1';
};

const UpgradePage = () => {
  const { userProfile } = useAuthContext();
  const currentTier = normalizeTier(userProfile?.tier);
  const [estTier, setEstTier] = useState(2);
  const [estTerm, setEstTerm] = useState(6);

  useEffect(() => {
    const id = 'qfaa-pricing-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href =
      'https://fonts.googleapis.com/css2?family=Poppins:wght@500;600;700;800&family=Inter:wght@400;500;600&display=swap';
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    if (currentTier === 'tier2') setEstTier(3);
    else setEstTier(2);
  }, [currentTier]);

  const estTotal = useMemo(() => {
    const annual = ANNUAL[estTier];
    const rate = annual / 12;
    return roundUp25(rate * estTerm);
  }, [estTier, estTerm]);

  return (
    <div className="qfaa-pricing-page qfaa-upgrade-page">
      <header className="topbar">
        <div className="wrap">
          <nav className="nav">
            <Link className="brand" to="/" aria-label="JTNC Group home">
              <img className="logo" src="/logojt.png" alt="JTNC Group logo" />
              <span className="brand-name">JTNC Group, LLC</span>
            </Link>
          </nav>
        </div>
      </header>

      <main>
        <section className="phead">
          <div className="wrap">
            <span className="eyebrow">
              <span className="dot" /> QFAA · Upgrade
            </span>
            <h1>Upgrade your QFAA Tier.</h1>
            <p>
              Raise your risk-per-trade cap as your account grows. Everything else about your
              subscription stays the same.
            </p>
          </div>
        </section>

        <section className="wrap">
          <div className="tier-grid">
            {TIERS.map((tier) => {
              const isCurrent = currentTier === tier.id;
              const className = [
                'tcard',
                isCurrent ? 'current' : '',
                tier.featured && !isCurrent ? 'featured' : '',
              ]
                .filter(Boolean)
                .join(' ');

              return (
                <article key={tier.id} className={className}>
                  {isCurrent ? <span className="badge-now">Your current Tier</span> : null}
                  <span className="tno">{tier.no}</span>
                  <h3>{tier.title}</h3>
                  <div className="thero">
                    <span className={`p ${tier.priceFree ? 'free' : ''}`}>{tier.priceLabel}</span>
                    {tier.priceSuffix ? <span className="lbl">{tier.priceSuffix}</span> : null}
                  </div>
                  {tier.proNote ? <div className="pro-note">{tier.proNote}</div> : null}
                  {tier.saveNote ? (
                    <div className="tsave" title={tier.saveNote.title}>
                      {tier.saveNote.text} <em>per $ of risk</em>
                    </div>
                  ) : null}
                  <div className="trisk">{tier.riskLabel}</div>
                  {tier.singlePayNote ? (
                    <div className="sp-note">
                      {CHECK}
                      <span>
                        <b>Single Pay clients:</b> {tier.singlePayNote}
                      </span>
                    </div>
                  ) : null}
                  <p className="sub">{tier.sub}</p>
                  <ul className="feats">
                    {tier.features.map((f) => (
                      <li key={f}>
                        {CHECK}
                        {f}
                      </li>
                    ))}
                  </ul>
                </article>
              );
            })}
          </div>

          <div className="est">
            <h3>Choose your upgrade term</h3>
            <p className="est-intro">
              Pick the term that matches the months left in your subscription year. Pay As You Go
              clients can go month-to-month at the 1-month rate.
            </p>
            <div className="est-mid">
              Mid-month? Don&apos;t worry — have 6½ months left? Pay for 6 and we&apos;ll cover the
              extra half month at no charge.
            </div>
            <div className="est-row">
              <span className="est-lbl">Upgrade to</span>
              <div className="seg" role="group" aria-label="Upgrade tier">
                <button
                  type="button"
                  className={`segb${estTier === 2 ? ' on' : ''}`}
                  onClick={() => setEstTier(2)}
                  disabled={currentTier === 'tier2' || currentTier === 'tier3'}
                >
                  Tier 2 · $1,000 risk
                </button>
                <button
                  type="button"
                  className={`segb${estTier === 3 ? ' on' : ''}`}
                  onClick={() => setEstTier(3)}
                  disabled={currentTier === 'tier3'}
                >
                  Tier 3 · $2,000 risk
                </button>
              </div>
            </div>
            <div className="est-row">
              <span className="est-lbl">Upgrade term</span>
              <select
                aria-label="Upgrade term"
                value={estTerm}
                onChange={(e) => setEstTerm(Number(e.target.value))}
              >
                <option value={1}>1 month</option>
                <option value={3}>3 months</option>
                <option value={6}>6 months</option>
                <option value={12}>12 months (full year)</option>
              </select>
            </div>
            <div className="est-out">
              <div className="est-price">
                {formatMoney(estTotal)}{' '}
                <em>
                  for a {estTerm}-month Tier {estTier} upgrade
                </em>
              </div>
            </div>
            <p className="est-fine">
              Have questions and want to speak to someone before upgrading? Contact us.
            </p>
            <a
              className="tcta solid"
              style={{ maxWidth: 260 }}
              href={CONTACT_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Contact us to upgrade
            </a>
          </div>

          <div style={{ textAlign: 'center' }}>
            <Link className="up-back" to="/account">
              ← Back to Account
            </Link>
          </div>
        </section>
      </main>

      <footer>
        <div className="wrap">
          <div className="foot">
            <div className="links">
              <Link to="/terms">Terms of Use</Link>
              <Link to="/privacy">Privacy Policy</Link>
              <Link to="/disclaimer">Disclaimer</Link>
            </div>
            <div className="integ">
              Integrated with
              <span className="chip">
                <svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <circle cx="16" cy="16" r="15" fill="#1769d6" />
                  <path
                    d="M9 19 L15 12 L19 16 L24 9"
                    fill="none"
                    stroke="#ff7a18"
                    strokeWidth="2.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle cx="24" cy="9" r="2.4" fill="#ff7a18" />
                </svg>
                <span className="word">
                  tradier<b>.</b>
                </span>
              </span>
            </div>
          </div>
          <p className="disc">
            Trading in securities and options carries substantial risk and is not suitable for every
            investor. Prices shown are subscription fees for automated trading software and do not
            include brokerage commissions or market data. Past performance is not indicative of
            future results. The initiation fee for Quantum Superstars covers the first month of
            service.
          </p>
          <p className="copy">© 2026 JTNCgroup.com. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default UpgradePage;
